import { useEffect, useMemo, useState } from 'react'
import { AccountLink, DrillValue } from '../components/Drill'
import { listCrashes, type RemoteCrash } from '../lib/adminClient'
import { LoadError } from '../components/LoadError'
import { DetailGrid } from '../components/RowDetail'

// Crash reports from the desktop app (GET /admin/crashes), newest first.
// Read-only. Only accounts that turned on crash reporting send them, the server
// shows them for a limited time (the response says how long; then they are
// archived, unread, and erased when the archive period ends), and home-folder
// user names are scrubbed from paths before they are stored. So this is a
// recent, opted-in sample of failures, not a count of every crash.

function fmt(ts: number): string {
  return new Date(ts).toISOString().slice(0, 16).replace('T', ' ') + ' UTC'
}

export default function Crashes(): JSX.Element {
  const [crashes, setCrashes] = useState<RemoteCrash[] | null>(null)
  const [retentionDays, setRetentionDays] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [version, setVersion] = useState<string>('')

  useEffect(() => {
    let cancelled = false
    void listCrashes(500).then((res) => {
      if (cancelled) return
      if (res.ok) {
        setCrashes(res.data.crashes)
        setRetentionDays(res.data.retentionDays)
      } else {
        setError(res.error)
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  const versions = useMemo(
    () => [...new Set((crashes ?? []).map((c) => c.appVersion).filter((v): v is string => !!v))].sort().reverse(),
    [crashes]
  )
  const visible = useMemo(
    () => (crashes ?? []).filter((c) => !version || c.appVersion === version),
    [crashes, version]
  )
  // Group identical messages so a crash loop reads as one problem, not fifty.
  const groups = useMemo(() => {
    const m = new Map<string, { message: string; count: number; accounts: Set<string>; latest: RemoteCrash; first: RemoteCrash; versions: Set<string> }>()
    for (const c of visible) {
      const key = `${c.kind}\u0000${c.message}`
      const g = m.get(key)
      if (g) {
        g.count++
        g.accounts.add(c.accountId)
        if (c.receivedAt < g.first.receivedAt) g.first = c
        if (c.appVersion) g.versions.add(c.appVersion)
      } else m.set(key, { message: c.message, count: 1, accounts: new Set([c.accountId]), latest: c, first: c, versions: new Set(c.appVersion ? [c.appVersion] : []) })
    }
    return [...m.values()].sort((a, b) => b.latest.receivedAt - a.latest.receivedAt)
  }, [visible])

  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Reliability</div>
        <h1 className="text-2xl font-semibold mt-1">Crash reports</h1>
        <div className="text-sm text-slate-400 mt-1 max-w-2xl">
          Sent only by accounts that turned on crash reporting in the app.
          {retentionDays !== null ? ` Kept ${retentionDays} days, then archived where nothing reads them, and erased when the archive period ends.` : ''} Home-folder user names are
          removed from paths. A sample of what is failing, not a count of every crash.
        </div>
      </div>

      {error && <LoadError what="crash reports" endpoint="GET /admin/crashes" error={error} />}
      {!error && crashes === null && <div className="glass-card rounded-xl p-6 text-sm text-slate-400">Loading crash reports…</div>}
      {crashes && crashes.length === 0 && (
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">
          No crash reports{retentionDays !== null ? ` in the last ${retentionDays} days` : ''}. Either nothing crashed
          for the people who share crash reports, or nobody has turned it on.
        </div>
      )}

      {crashes && crashes.length > 0 && (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
            <span className="text-slate-400">
              {visible.length} {visible.length === 1 ? 'report' : 'reports'} in {groups.length}{' '}
              {groups.length === 1 ? 'group' : 'groups'}
            </span>
            <select
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              className="rounded-md border border-white/10 bg-transparent px-2 py-1 text-slate-200"
            >
              <option value="">All versions</option>
              {versions.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            {groups.map((g) => {
              const c = g.latest
              const isOpen = open === c.id
              return (
                <div key={c.id} className="glass-card rounded-xl border border-white/5">
                  <button
                    onClick={() => setOpen(isOpen ? null : c.id)}
                    className="w-full text-left p-4 flex items-start justify-between gap-4"
                    aria-expanded={isOpen}
                    data-testid={`crash-${c.id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-100 break-words">{g.message}</div>
                      <div className="text-[11px] text-slate-400 mt-1">
                        {c.source} · {c.kind}
                        {c.appVersion ? ` · v${c.appVersion}` : ''}
                        {c.platform ? ` · ${c.platform}` : ''} · last {fmt(c.receivedAt)}
                      </div>
                    </div>
                    <div className="shrink-0 text-right text-[11px] text-slate-400">
                      <div className="text-slate-200 text-sm tabular-nums">{g.count}×</div>
                      <div>
                        {g.accounts.size} {g.accounts.size === 1 ? 'account' : 'accounts'}
                      </div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-white/5 p-4 space-y-3 text-[11px]" data-testid={`crash-${c.id}-detail`}>
                      <DetailGrid
                        facts={[
                          ['Latest report', <span className="font-mono break-all">{c.id}</span>],
                          ['Happened', fmt(c.ts)],
                          ['Received', fmt(c.receivedAt)],
                          ['Source', c.source],
                          ['Kind', c.kind],
                          ['App version', c.appVersion],
                          ['Platform', c.platform],
                          ['Times in this list', `${g.count}${version ? ` on v${version}` : ''}`],
                          ['First seen', fmt(g.first.receivedAt)],
                          ['Versions it happened on', [...g.versions].sort().reverse().join(', ')]
                        ]}
                        wide={[['Accounts it happened to', <span className="font-mono break-all">{[...g.accounts].join(', ')}</span>]]}
                      />
                      {c.context && (
                        <div>
                          <div className="text-slate-500 uppercase tracking-[0.16em] text-[10px] mb-1">Context</div>
                          <div className="text-slate-300 break-words">{c.context}</div>
                        </div>
                      )}
                      {c.stack && (
                        <div>
                          <div className="text-slate-500 uppercase tracking-[0.16em] text-[10px] mb-1">Stack</div>
                          <pre className="text-slate-300 whitespace-pre-wrap break-words max-h-80 overflow-auto">{c.stack}</pre>
                        </div>
                      )}
                      {c.componentStack && (
                        <div>
                          <div className="text-slate-500 uppercase tracking-[0.16em] text-[10px] mb-1">Component stack</div>
                          <pre className="text-slate-300 whitespace-pre-wrap break-words max-h-60 overflow-auto">{c.componentStack}</pre>
                        </div>
                      )}
                      <div className="text-slate-500">
                        Account <AccountLink id={c.accountId}><code className="text-slate-400">{c.accountId}</code></AccountLink> · occurred {fmt(c.ts)} ·{' '}
                        {/* The group is counted over the reports loaded here (the newest 500). */}
                        <DrillValue
                          to={{ set: 'crashes', filters: { kind: c.kind, message: g.message, ...(version ? { version } : {}) } }}
                          label={`Reports of “${g.message.slice(0, 60)}”`}
                          expect={{ key: 'count', value: g.count }}
                          testId={`crash-records-${c.id}`}
                        >
                          all {g.count} {g.count === 1 ? 'report' : 'reports'}
                        </DrillValue>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
